import { useState, useEffect } from 'react';
import Modal from '../modal/modal';
import Card from '../card/Card';
import Input from '../input/Input';
import Span from '../texts/span/Span';
import { toast } from 'react-toastify';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { useConfig } from '../../context/ConfigContext';
import { validateCuit } from '../../utils/validationUtils';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { ReferrerName } from '../../types/interfaces';

interface NewReferrerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  referrerToEdit?: ReferrerName | null;
}

const NewReferrerModal = ({
  isOpen,
  onClose,
  onSuccess,
  referrerToEdit = null,
}: NewReferrerModalProps) => {
  const fiscalId = useFiscalIdConfig();
  const config = useConfig();
  const showGuaranteesReferrerFields = config.isGuaranteesEnabled === true;
  const { createReferrer, updateReferrer, isCreating, isUpdating } = useReferrerNames(true);

  const [name, setName] = useState('');
  const [cuit, setCuit] = useState('');
  const [commissionPercentage, setCommissionPercentage] = useState('');

  const [errors, setErrors] = useState<{
    name?: string;
    cuit?: string;
    commissionPercentage?: string;
  }>({});

  const isEditMode = Boolean(referrerToEdit);
  const isSaving = isCreating || isUpdating;

  const resetForm = () => {
    setName('');
    setCuit('');
    setCommissionPercentage('');
    setErrors({});
  };

  useEffect(() => {
    if (!isOpen) return;
    if (referrerToEdit) {
      setName(referrerToEdit.name ?? '');
      setCuit(referrerToEdit.cuit ? fiscalId.format(referrerToEdit.cuit) : '');
      const commission = referrerToEdit.commissionPercentage ?? null;
      setCommissionPercentage(
        commission !== null && commission !== undefined
          ? String(commission)
          : '',
      );
      setErrors({});
    } else {
      resetForm();
    }
  }, [isOpen, referrerToEdit]);

  const handleCuitChange = (value: string) => {
    setCuit(fiscalId.format(value));
    if (errors.cuit) {
      setErrors((prev) => ({ ...prev, cuit: undefined }));
    }
  };

  const handleCommissionChange = (value: string) => {
    const cleaned = value.replace(/[^0-9.,]/g, '').replace(',', '.');
    const parts = cleaned.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    setCommissionPercentage(cleaned);
    if (errors.commissionPercentage) {
      setErrors((prev) => ({ ...prev, commissionPercentage: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    if (showGuaranteesReferrerFields) {
      const cuitDigits = cuit.replace(/\D/g, '');
      if (cuitDigits.length > 0) {
        if (!fiscalId.isValidDigits(cuitDigits)) {
          newErrors.cuit = fiscalId.invalidMessage;
        } else if (fiscalId.country === 'AR' && validateCuit(cuitDigits)) {
          // UY no tiene dígito verificador definido acá; AR mantiene el checksum.
          newErrors.cuit = fiscalId.invalidMessage;
        }
      }

      if (!commissionPercentage.trim()) {
        newErrors.commissionPercentage = '% Comisión es obligatorio';
      } else {
        const numValue = parseFloat(commissionPercentage.replace(',', '.'));
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          newErrors.commissionPercentage = 'Debe estar entre 0 y 100';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const cuitDigits = cuit.replace(/\D/g, '');
    const cuitToSend = showGuaranteesReferrerFields
      ? fiscalId.isValidDigits(cuitDigits)
        ? cuitDigits
        : null
      : referrerToEdit?.cuit ?? null;
    const commissionValue = showGuaranteesReferrerFields
      ? parseFloat(commissionPercentage.replace(',', '.'))
      : referrerToEdit?.commissionPercentage ?? 0;

    if (isEditMode && referrerToEdit) {
      const result = await updateReferrer(
        referrerToEdit.id,
        name.trim(),
        referrerToEdit.code,
        cuitToSend,
        commissionValue,
        referrerToEdit.enabled,
      );
      if (result.success) {
        toast.success('Referido actualizado exitosamente');
        resetForm();
        onClose();
        onSuccess?.();
      } else {
        toast.error(result.message || 'Error al actualizar el referido');
      }
    } else {
      const result = await createReferrer(
        name.trim(),
        '',
        cuitToSend,
        commissionValue,
      );
      if (result.success) {
        toast.success('Referido creado exitosamente');
        resetForm();
        onClose();
        onSuccess?.();
      } else {
        toast.error(result.message || 'Error al crear el referido');
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Editar referido' : 'Nuevo referido'}
      width='600px'
      primaryText={isEditMode ? 'Guardar' : 'Crear referido'}
      primaryVariant='primary'
      onPrimaryAction={handleSubmit}
      primaryActionDisabled={isSaving}
      secondaryText='Cancelar'
      secondaryVariant='contained'
      onSecondaryAction={handleClose}
      showOverlay={true}
    >
      <Card margin={{ top: '4' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <div>
            <Input
              label='Nombre *'
              name='name'
              type='text'
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              disabled={isSaving}
              size='md'
              inputStatus={errors.name ? 'error' : 'normal'}
              placeholder='Nombre o razón social del referido'
              maxLength={500}
            />
            {errors.name && (
              <Span variant='danger' type='2'>
                {errors.name}
              </Span>
            )}
          </div>

          {showGuaranteesReferrerFields && (
            <>
              <div>
                <Input
                  label={`${fiscalId.cuilLabel} (opcional)`}
                  name='cuit'
                  type='text'
                  value={cuit}
                  onChange={(e) => handleCuitChange(e.target.value)}
                  disabled={isSaving}
                  size='md'
                  inputStatus={errors.cuit ? 'error' : 'normal'}
                  placeholder={fiscalId.country === 'UY' ? 'RUT o Cédula' : 'XX-XXXXXXXX-X'}
                  maxLength={fiscalId.country === 'UY' ? 12 : 13}
                />
                {errors.cuit && (
                  <Span variant='danger' type='2'>
                    {errors.cuit}
                  </Span>
                )}
              </div>

              <div>
                <Input
                  label='% Comisión *'
                  name='commissionPercentage'
                  type='text'
                  value={commissionPercentage}
                  onChange={(e) => handleCommissionChange(e.target.value)}
                  disabled={isSaving}
                  size='md'
                  inputStatus={errors.commissionPercentage ? 'error' : 'normal'}
                  placeholder='Ej: 1,50'
                  maxLength={6}
                />
                {errors.commissionPercentage && (
                  <Span variant='danger' type='2'>
                    {errors.commissionPercentage}
                  </Span>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </Modal>
  );
};

export default NewReferrerModal;
